import './WarningAndAlertTable.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Icons,
  PendingContent,
  Spinner,
  Status,
  TableV2,
  StatusTimeline,
} from 'plume-ui';
import { ColumnType } from 'plume-ui/dist/components/TableV2/types';
import { useApolloClient } from '@apollo/client';

import useAutonomousMonitors from '../../../Monitor/hooks/useAutonomousMonitors';
import { AutonomousMonitor } from '../../../Monitor/types';
import {
  getAutonomousMonitorLatestGraph,
  getAutonomousMonitorOutreachEvents,
} from '../../../Monitor/services/monitorQueries';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Routes, STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import { useRecoilValue } from 'recoil';
import { activePartnerAtom } from '../../../../state';

dayjs.extend(duration);
dayjs.extend(relativeTime);

type MonitorEvent = {
  monitorId: string;
  event_type: 'warning' | 'alert';
  start_time: string;
  end_time?: string;
};

type MonitorRowData = AutonomousMonitor & {
  monitorStatus: 'normal' | 'warning' | 'alert';
  statusColor: 'ok' | 'warning' | 'error';
  locationsImpacted: number | string;
  startTime: string;
  durationText: string;
  duration: number;
  latestEventTime?: string;
  hasActiveEvent: boolean;
  events: MonitorEvent[];
};

const useAutonomousMonitorsGraphData = () => {
  const { data: autonomousMonitors, isLoading } = useAutonomousMonitors();
  const apollo = useApolloClient();

  const [monitorsWithGraphData, setMonitorsWithGraphData] = useState<
    MonitorRowData[]
  >([]);
  const [isLoadingGraphData, setIsLoadingGraphData] = useState(false);

  useEffect(() => {
    if (!autonomousMonitors) {
      setIsLoadingGraphData(false);
      return;
    }

    const fetchMonitorData = async () => {
      setIsLoadingGraphData(true);
      try {
        const results = await Promise.all(
          autonomousMonitors.map(async (monitor) => {
            const [eventsResponse, graphResponse] = await Promise.all([
              apollo.query({
                query: getAutonomousMonitorOutreachEvents(monitor.id),
              }),
              apollo.query({
                query: getAutonomousMonitorLatestGraph(monitor.id),
              }),
            ]);

            const sortedEvents = eventsResponse.data?.events
              ? [...eventsResponse.data.events].sort((a, b) =>
                  dayjs(b.start_time).diff(dayjs(a.start_time)),
                )
              : [];

            // Highest severity filter
            const latestUnresolvedEvent = sortedEvents
              .filter((item) => !item.end_time)
              .reduce((highest, current) => {
                if (!highest) return current;
                if (current.event_type === 'alert') return current;
                if (highest.event_type === 'alert') return highest;
                return highest;
              }, null as typeof sortedEvents[0] | null);
            const latestEvent = latestUnresolvedEvent || sortedEvents[0];

            let latestData;
            if (graphResponse.data?.latest_data) {
              const yellowSorted = [
                ...graphResponse.data.latest_data.yellow,
              ].sort((a, b) => dayjs(a.ts).diff(dayjs(b.ts)));
              const redSorted = [
                ...graphResponse.data.latest_data.red,
              ].sort((a, b) => dayjs(a.ts).diff(dayjs(b.ts)));

              latestData = {
                yellow: yellowSorted[yellowSorted.length - 1],
                red: redSorted[redSorted.length - 1],
              };
            }

            const hasActiveEvent = Boolean(
              latestEvent && !latestEvent.end_time,
            );

            // Only show status, duration, start time, and locations for active events
            const monitorStatus: 'normal' | 'warning' | 'alert' = hasActiveEvent
              ? latestEvent.event_type
              : 'normal';
            const statusColor = {
              normal: 'ok',
              warning: 'warning',
              alert: 'error',
            }[monitorStatus] as 'ok' | 'warning' | 'error';

            const locationsImpacted = hasActiveEvent
              ? (latestData?.yellow?.location_count || 0) +
                (latestData?.red?.location_count || 0)
              : '-';

            const startTime =
              hasActiveEvent && latestEvent?.start_time
                ? dayjs(latestEvent.start_time)
                    .local()
                    .format(STANDARD_DATE_TIME_FORMAT)
                : '-';

            let durationText = '-';
            if (hasActiveEvent && latestEvent?.start_time) {
              const start = dayjs(latestEvent.start_time);
              const end = dayjs();
              const diffMs = end.diff(start);

              const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const hours = Math.floor(
                (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
              );
              const minutes = Math.floor(
                (diffMs % (1000 * 60 * 60)) / (1000 * 60),
              );

              if (days > 0) {
                durationText = `${days}d ${hours}h`;
              } else if (hours > 0) {
                durationText = `${hours}h ${minutes}m`;
              } else {
                durationText = `${minutes}m`;
              }
            }

            return {
              ...monitor,
              monitorStatus,
              statusColor,
              locationsImpacted,
              startTime,
              duration: dayjs().diff(dayjs(latestEvent?.start_time)),
              durationText,
              latestEventTime: latestEvent?.start_time,
              hasActiveEvent,
              events: sortedEvents,
            };
          }),
        );

        setMonitorsWithGraphData(results);
      } finally {
        setIsLoadingGraphData(false);
      }
    };

    fetchMonitorData();
  }, [autonomousMonitors, apollo]);

  return useMemo(
    () => ({
      monitorsWithGraphData,
      isLoading: isLoading || isLoadingGraphData,
    }),
    [monitorsWithGraphData, isLoading, isLoadingGraphData],
  );
};

const WarningAndAlertTable = () => {
  const navigate = useRedirectToRoute();
  const activePartner = useRecoilValue(activePartnerAtom);
  const { t } = useTranslation();

  const { monitorsWithGraphData, isLoading } = useAutonomousMonitorsGraphData();

  // Sort monitors by most recent issues
  const sortedMonitors = useMemo(() => {
    if (!monitorsWithGraphData.length) return [];

    return [...monitorsWithGraphData].sort((a, b) => {
      // First, prioritize monitors with active (unresolved) events
      if (a.hasActiveEvent && !b.hasActiveEvent) return -1;
      if (!a.hasActiveEvent && b.hasActiveEvent) return 1;

      // Then sort by latest event time
      if (a.latestEventTime && b.latestEventTime) {
        return (
          dayjs(b.latestEventTime).valueOf() -
          dayjs(a.latestEventTime).valueOf()
        );
      }

      if (a.latestEventTime && !b.latestEventTime) return -1;
      if (!a.latestEventTime && b.latestEventTime) return 1;

      // Finally, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }, [monitorsWithGraphData]);

  const TABLE_COLUMNS: ColumnType<MonitorRowData>[] = useMemo(
    (): ColumnType<MonitorRowData>[] => [
      {
        title: t('healthSummary.warningAndAlertTable.columns.alert'),
        key: 'alert',
        dataIndex: 'name',
        width: 200,
        render: (row) => (
          <div className="WarningAndAlertTable__alertCell">
            <Status color={row.statusColor} />
            <span>{row.name}</span>
          </div>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: t('healthSummary.warningAndAlertTable.columns.startTime'),
        key: 'startTime',
        dataIndex: 'startTime',
        sorter: (a, b) => {
          const aTime = a.latestEventTime
            ? dayjs(a.latestEventTime).valueOf()
            : 0;
          const bTime = b.latestEventTime
            ? dayjs(b.latestEventTime).valueOf()
            : 0;
          return bTime - aTime;
        },
      },
      {
        title: t('healthSummary.warningAndAlertTable.columns.duration'),
        key: 'duration',
        dataIndex: 'durationText',
        sorter: (a, b) => b.duration - a.duration,
      },
      {
        title: t(
          'healthSummary.warningAndAlertTable.columns.locationsImpacted',
        ),
        key: 'locationsImpacted',
        dataIndex: 'locationsImpacted',
        render: (row) => row.locationsImpacted.toLocaleString(),
        sorter: (a, b) => {
          const aCount =
            typeof a.locationsImpacted === 'number' ? a.locationsImpacted : -1;
          const bCount =
            typeof b.locationsImpacted === 'number' ? b.locationsImpacted : -1;
          return bCount - aCount;
        },
      },
      {
        title: t('healthSummary.warningAndAlertTable.columns.alertHistory'),
        key: 'alertHistory',
        width: 300,
        render: (row) => {
          // 72-hour timeline, last 3 days
          const now = dayjs();
          const timelineData = [];

          for (let i = 71; i >= 0; i--) {
            const hourStart = now.subtract(i, 'hour').startOf('hour');
            const hourEnd = hourStart.endOf('hour');

            // Check if there's an event during this hour
            let maxSeverity: 'normal' | 'warning' | 'alert' = 'normal';

            if (row.events && row.events.length > 0) {
              for (const event of row.events) {
                const eventStart = dayjs(event.start_time);
                const eventEnd = event.end_time ? dayjs(event.end_time) : now;

                // Check if this event overlaps with the current hour
                if (
                  eventStart.isBefore(hourEnd) &&
                  eventEnd.isAfter(hourStart)
                ) {
                  // Update max severity if this event is more severe
                  if (event.event_type === 'alert') {
                    maxSeverity = 'alert';
                  } else if (
                    event.event_type === 'warning' &&
                    maxSeverity !== 'alert'
                  ) {
                    maxSeverity = 'warning';
                  }
                }
              }
            }

            const color =
              maxSeverity === 'alert'
                ? '#dc0462'
                : maxSeverity === 'warning'
                ? '#ffc501'
                : '#00ca72';

            timelineData.push({
              timestamp: hourStart.toISOString(),
              color,
              label: '', // Not visible anyways
            });
          }

          return (
            <div className="WarningAndAlertTable__alertHistoryCell">
              <StatusTimeline
                className="WarningAndAlertTable__statusTimeline"
                data={timelineData}
                labelFormatFunction={(t) => dayjs(t).local().format('ddd')}
                timestampFormatFunction={(timestamp) =>
                  dayjs(timestamp).local().format('ddd')
                }
              />
            </div>
          );
        },
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <PendingContent loading loader={Spinner}>
        <div style={{ height: 300 }}></div>
      </PendingContent>
    );
  }

  return (
    <div className="WarningAndAlertTable">
      <div className="WarningAndAlertTable__title">
        <span>{t('healthSummary.warningAndAlertTable.title')}</span>
      </div>

      {(monitorsWithGraphData?.length ?? 0) === 0 && (
        <div className="WarningAndAlertTable__noMonitors">
          <Icons.NetworkIcon width={32} height={32} />
          {t('healthSummary.cspOutageMap.contactPlumeRep')}
        </div>
      )}

      {(monitorsWithGraphData?.length ?? 0) > 0 && (
        <TableV2
          columns={TABLE_COLUMNS}
          data={sortedMonitors || []}
          rowKey={(row: any) => row.id}
          expandable={{
            showExpandColumn: false, // unfortunate hack to have a clickable row
            expandedRowRender: () => null,
            rowExpandable: () => true,
            expandRowByClick: true,
            onExpand: (_exp, row) => {
              navigate(Routes.AutonomousMonitorDetails, {
                partnerId: activePartner?.id,
                monitorId: row.id,
                eventId: 0,
              });
            },
          }}
        />
      )}
    </div>
  );
};

export default WarningAndAlertTable;
